/**
 * Send an estimate — one request, seen by a builder, beside the estimate form.
 *
 * The builder prices ONE total; Twasta's standard stages (instance admin terms,
 * `terms.stages`) split it, and the builder says what they deliver at each
 * stage and when. The monthly price to use the app once installed must be above
 * Twasta's hosting cost (checked again by the server on send). Saving after a
 * version was sent starts the next version (saveQuoteDraft does that).
 * Doc: docs/marketplace_get_it_built.md
 */
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { Link, useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { fmtDate, money, mktError, type BuildTerms } from '@/api/marketplace'
import {
  getWork, requestMessages, saveQuoteDraft, sendQuote, sendRequestMessage, withdrawQuote,
  type BuildRequest, type Quote, type QuoteInput, type TimelineEvent,
} from '@/api/marketplaceBuild'
import MarketplaceShell from '../MarketplaceShell'
import HostCallout, { useHosting } from './HostCallout'
import PaymentStages from './PaymentStages'
import { budgetText, EligibleBanner, KindPill, Loading, QuotePill, relTime } from './builderUi'

interface Demo { name: string; url: string; note: string }
interface Form {
  price: string
  plan: { deliverables: string; weeks: string }[]
  coverage: Record<string, { included: boolean; note: string }>
  basis: 'person' | 'server'
  monthly: string
  size: string
  answers: string[]
  note: string
  demos: Demo[]
  start: string
  ai: 'included' | 'client'
}

const DIRECTION: Record<string, string> = { in: 'Into the app', out: 'From the app to their system', both: 'Both ways' }
const toMajor = (minor: number | null | undefined) => (minor ? String(minor / 100) : '')
const toMinor = (s: string) => Math.round((Number(String(s).replace(/[^\d.]/g, '')) || 0) * 100)
const sym = (cur: string) => (cur === 'INR' ? '₹' : cur === 'USD' ? '$' : cur)

function briefItems(r: BuildRequest): string[] {
  return (r.brief || []).flatMap((s) => s.items || [])
}

function formFrom(r: BuildRequest, q: Quote | null, terms: BuildTerms): Form {
  const cov: Form['coverage'] = {}
  for (const item of briefItems(r)) {
    const c = q?.coverage?.[item]
    cov[item] = { included: c ? c.included : true, note: c?.note || '' }
  }
  const m = (q?.monthly || {}) as Partial<{ basis: 'person' | 'server'; price_minor: number; size: string }>
  return {
    price: toMajor(q?.price_minor),
    plan: terms.stages.map((_, i) => ({ deliverables: q?.stages?.[i]?.deliverables || '', weeks: q?.stages?.[i]?.weeks ? String(q.stages[i].weeks) : '' })),
    coverage: cov,
    basis: m.basis === 'server' ? 'server' : 'person',
    monthly: toMajor(m.price_minor),
    size: m.size || '',
    answers: (r.questions || []).map((_, i) => q?.answers?.[i] || ''),
    note: q?.note || '',
    demos: (q?.demos || []).map((d) => ({ name: d.name || '', url: d.url || '', note: d.note || '' })),
    start: q?.start_date ? q.start_date.slice(0, 10) : '',
    ai: q?.ai_mode === 'client' ? 'client' : 'included',
  }
}

/** Same split as the server and PaymentStages: each share rounded, the last stage takes the rest. */
function split(pcts: number[], amount: number): number[] {
  let run = 0
  return pcts.map((p, i) => { const a = i === pcts.length - 1 ? amount - run : Math.round((amount * p) / 100); run += a; return a })
}

export default function SendEstimatePage() {
  const { requestId = '' } = useParams()
  const rid = Number(requestId)
  const [req, setReq] = useState<BuildRequest | null>(null)
  const [quote, setQuote] = useState<Quote | null>(null)
  const [terms, setTerms] = useState<BuildTerms | null>(null)
  const [eligible, setEligible] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [form, setForm] = useState<Form | null>(null)
  const [editing, setEditing] = useState(false)
  const [busy, setBusy] = useState<'' | 'save' | 'send' | 'withdraw'>('')
  const [problems, setProblems] = useState<string[]>([])
  const [confirmWithdraw, setConfirmWithdraw] = useState(false)
  const [justSent, setJustSent] = useState(false)

  const load = useCallback(async () => {
    if (!rid) { setError("We couldn't find that request."); return }
    try {
      const w = await getWork(rid)
      setReq(w.request); setQuote(w.quote); setTerms(w.terms); setEligible(w.eligible)
      setForm(formFrom(w.request, w.quote, w.terms))
      setEditing(!w.quote || w.quote.status === 'draft')
      setProblems(w.quote?.status === 'draft' ? w.quote.problems || [] : [])
      setError(null)
    } catch (e) {
      setError(mktError(e, "We couldn't load this request.").message)
    }
  }, [rid])
  useEffect(() => { load() }, [load])

  const hosting = useHosting(req?.currency || 'INR')
  useEffect(() => {
    if (form && form.basis === 'server' && !form.size && hosting?.sizes.length) setForm({ ...form, size: hosting.sizes[0].id })
  }, [form, hosting])

  if (error) return <MarketplaceShell><div className="mkt-wrap" style={{ padding: '32px 20px' }}><div role="alert" className="mkt-banner bad"><div><strong>Something went wrong</strong>{error}</div><Link to="/marketplace/work" className="btn btn-secondary">Back to Find work</Link></div></div></MarketplaceShell>
  if (!req || !terms || !form) return <MarketplaceShell><Loading text="Loading the request…" /></MarketplaceShell>

  const cur = req.currency
  const open = req.status === 'open'
  const accepted = quote?.status === 'accepted'
  const canEdit = open && !eligible && !accepted
  const formOn = canEdit && editing
  const set = (patch: Partial<Form>) => setForm({ ...form, ...patch })

  const body = (): QuoteInput => ({
    price_minor: toMinor(form.price),
    plan: form.plan.map((p, i) => ({ deliverables: i === 0 ? '' : p.deliverables, weeks: i === 0 ? 0 : Math.max(0, Math.round(Number(p.weeks) || 0)) })),
    coverage: form.coverage,
    monthly: form.basis === 'server'
      ? { basis: 'server', price_minor: toMinor(form.monthly), size: form.size }
      : { basis: 'person', price_minor: toMinor(form.monthly) },
    ai_mode: form.ai,
    note: form.note,
    answers: form.answers,
    demos: form.demos.filter((d) => d.name.trim()),
    start_date: form.start || null,
  })

  const save = async (): Promise<Quote | null> => {
    try {
      const q = await saveQuoteDraft(rid, body())
      setQuote(q)
      setProblems(q.problems || [])
      return q
    } catch (e) {
      const err = mktError(e, "We couldn't save your estimate.")
      setProblems(Array.isArray(err.extra.problems) ? err.extra.problems : [err.message])
      toast.error(err.message)
      return null
    }
  }
  const onSave = async () => {
    setBusy('save')
    const q = await save()
    setBusy('')
    if (q) toast.success(q.problems?.length ? 'Draft saved — a few things are still missing' : 'Draft saved')
  }
  const onSend = async (e?: FormEvent) => {
    e?.preventDefault()
    setBusy('send')
    try {
      const draft = await save()
      if (!draft) return
      if (draft.problems?.length) { toast.error(draft.problems[0]); return }
      await sendQuote(draft.id)
      toast.success('Estimate sent')
      setJustSent(true)
      await load()
    } catch (err) {
      const m = mktError(err, "We couldn't send your estimate.")
      setProblems(Array.isArray(m.extra.problems) ? m.extra.problems : [m.message])
      toast.error(m.message)
    } finally {
      setBusy('')
    }
  }
  const onWithdraw = async () => {
    if (!quote) return
    setBusy('withdraw')
    try {
      await withdrawQuote(quote.id)
      toast.success('Estimate withdrawn')
      setConfirmWithdraw(false)
      setJustSent(false)
      await load()
    } catch (e) {
      toast.error(mktError(e, "We couldn't withdraw your estimate.").message)
    } finally {
      setBusy('')
    }
  }

  const priceMinor = toMinor(form.price)
  const pcts = terms.stages.map((s) => s.pct)
  const serverRows = quote && quote.price_minor === priceMinor && quote.stages?.length === terms.stages.length ? quote.stages : null
  const amounts = serverRows ? serverRows.map((s) => s.amount_minor) : split(pcts, priceMinor)
  const weeks = form.plan.reduce((a, p, i) => a + (i === 0 ? 0 : Math.max(0, Number(p.weeks) || 0)), 0)
  const items = briefItems(req)
  const covered = items.filter((i) => form.coverage[i]?.included !== false).length
  const feePct = terms.builder_fee_percent
  const tdsPct = terms.tds_percent?.[cur] ?? 0
  const feeMinor = Math.round((priceMinor * feePct) / 100)
  const tdsMinor = Math.round((priceMinor * tdsPct) / 100)
  const clientFeeMinor = Math.round((priceMinor * terms.client_fee_percent) / 100)

  const monthlyMinor = toMinor(form.monthly)
  const size = hosting?.sizes.find((s) => s.id === form.size)
  const hostMinor = form.basis === 'server' ? size?.price_minor ?? null : hosting?.per_person_minor ?? null
  const belowHost = hostMinor != null && monthlyMinor > 0 && monthlyMinor <= hostMinor
  const live = quote && quote.status !== 'draft' && !editing

  return (
    <MarketplaceShell>
      <div className="mkt-wrap mkt-col" style={{ padding: '22px 20px 64px', gap: 16 }}>
        <Link to="/marketplace/work" style={{ fontSize: 14 }}>← Back to Find work</Link>
        <EligibleBanner eligible={eligible} />
        {!open && <div role="status" className="mkt-banner plain"><div><strong>This request is closed for estimates</strong>{req.status === 'ordered' ? 'The client has placed an order.' : 'The client is no longer taking estimates.'}</div></div>}

        {quote && quote.status !== 'draft' && (
          <QuoteStatusPanel quote={quote} justSent={justSent} canEdit={canEdit && !editing} onEdit={() => { setEditing(true); setJustSent(false) }}
            confirmWithdraw={confirmWithdraw} setConfirmWithdraw={setConfirmWithdraw} onWithdraw={onWithdraw} busy={busy === 'withdraw'} />
        )}

        <div className="mkt-split">
          {/* ── the request ── */}
          <section className="mkt-col" style={{ flex: '1 1 400px', maxWidth: 520, minWidth: 0, gap: 14 }} aria-label="The request">
            <div className="mkt-panel mkt-col" style={{ gap: 12 }}>
              <div className="mkt-row" style={{ gap: 6 }}>
                <KindPill kind={req.kind} label={req.kind_label} />
                {req.invited_you && <span className="mkt-pill warn">You were invited</span>}
                <span className="mkt-faint">{req.posted_at ? `Posted ${relTime(req.posted_at)}` : ''}{req.closes_at && open ? ` · closes ${relTime(req.closes_at)}` : ''}</span>
              </div>
              <h1 style={{ fontSize: 26 }}>{req.title || 'Untitled request'}</h1>
              {req.description && <p className="mkt-muted" style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{req.description}</p>}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10, fontSize: 14, background: 'var(--color-surface-2)', borderRadius: 12, padding: '12px 14px' }}>
                <Fact label="Budget" value={budgetText(req)} />
                <Fact label="Pays" value="Fixed price, standard stages" />
                <Fact label="Needed" value={[req.timeline, req.start_date ? `from ${fmtDate(req.start_date)}` : ''].filter(Boolean).join(' · ') || 'Not said'} />
                {req.details?.users && <Fact label="Users" value={req.details.users} />}
                <Fact label="Selling it to others" value={req.listing_allowed ? 'Allowed — you may also sell it' : 'Not allowed — exclusive'} />
                <Fact label="Pays to use it" value="Monthly, after install" />
              </div>
              {req.feature_labels?.length > 0 && <div className="mkt-row" style={{ gap: 6 }}>{req.feature_labels.map((f) => <span key={f} className="mkt-pill">{f}</span>)}</div>}
              {req.details?.data && <Fact label="Their data today" value={req.details.data} />}
              {req.details?.integration?.system && (
                <div className="mkt-col" style={{ gap: 4, fontSize: 14 }}>
                  <strong>Integrate with their system</strong>
                  <span>{req.details.integration.system}{req.details.integration.version ? ` ${req.details.integration.version}` : ''} · {DIRECTION[req.details.integration.direction] || req.details.integration.direction}</span>
                  {req.details.integration.what && <span className="mkt-muted">{req.details.integration.what}</span>}
                  {req.details.integration.access && <span className="mkt-faint">Access: {req.details.integration.access}</span>}
                  {req.details.integration.docs && <span className="mkt-faint">Documentation: {req.details.integration.docs}</span>}
                </div>
              )}
              {req.details?.analytics?.db && (
                <div className="mkt-col" style={{ gap: 4, fontSize: 14 }}>
                  <strong>Their existing data</strong>
                  <span>{req.details.analytics.db}{req.details.analytics.app ? ` · filled by ${req.details.analytics.app}` : ''}{req.details.analytics.size ? ` · ${req.details.analytics.size}` : ''}</span>
                  {req.details.analytics.access && <span className="mkt-faint">Access: {req.details.analytics.access}</span>}
                </div>
              )}
              {(req.requirements?.certified || req.requirements?.rated || req.requirements?.overlap) && (
                <div className="mkt-row" style={{ gap: 6 }}>
                  {req.requirements.certified && <span className="mkt-pill">Wants a certified builder</span>}
                  {req.requirements.rated && <span className="mkt-pill">Wants a well-rated builder</span>}
                  {req.requirements.overlap && <span className="mkt-pill">Wants overlapping working hours</span>}
                </div>
              )}
            </div>

            <div className="mkt-panel mkt-col" style={{ gap: 10 }}>
              <div className="mkt-row" style={{ justifyContent: 'space-between' }}>
                <strong style={{ fontSize: 17 }}>The brief — what will you deliver?</strong>
                {items.length > 0 && <span className={`mkt-pill ${covered === items.length ? 'ok' : 'warn'}`}>{covered} of {items.length} included</span>}
              </div>
              {items.length === 0 && <span className="mkt-muted" style={{ fontSize: 14 }}>The client didn't list brief items — use the description above.</span>}
              {items.length > 0 && <span className="mkt-faint">The client compares estimates item by item. Leaving something out is fine — just say so.</span>}
              {(req.brief || []).map((sec, si) => (
                <div key={`${sec.h}-${si}`} className="mkt-col" style={{ gap: 6 }}>
                  {sec.h && <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '.04em' }}>{sec.h}</span>}
                  {(sec.items || []).map((item, ii) => {
                    const c = form.coverage[item] || { included: true, note: '' }
                    return (
                      <div key={`${item}-${ii}`} className="mkt-col" style={{ gap: 6, padding: '8px 0', borderBottom: '1px solid var(--color-border)' }}>
                        <div className="mkt-row" style={{ flexWrap: 'nowrap', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: 14 }}>{item}</span>
                          <button type="button" disabled={!formOn} aria-pressed={c.included}
                            className={`btn btn-sm ${c.included ? 'btn-secondary' : 'btn-ghost'}`}
                            style={{ color: c.included ? 'var(--color-success)' : 'var(--color-error)', flex: '0 0 auto' }}
                            onClick={() => set({ coverage: { ...form.coverage, [item]: { ...c, included: !c.included } } })}>
                            {c.included ? '✓ Included' : 'Not included'}
                          </button>
                        </div>
                        {!c.included && (
                          <input aria-label={`Why “${item}” is not included`} disabled={!formOn} placeholder="Say why, or what you suggest instead" value={c.note}
                            onChange={(e) => set({ coverage: { ...form.coverage, [item]: { ...c, note: e.target.value } } })}
                            style={{ font: 'inherit', fontSize: 14, padding: '8px 10px', borderRadius: 8, border: '1px solid var(--color-border-strong, var(--color-border))', background: 'var(--color-input-bg, var(--color-bg))', color: 'var(--color-text)' }} />
                        )}
                      </div>
                    )
                  })}
                </div>
              ))}
            </div>

            {req.client && (
              <div className="mkt-panel mkt-col" style={{ gap: 4, padding: 18, fontSize: 14 }}>
                <strong>About the client</strong>
                <span className="mkt-muted">
                  {req.client.payment_verified ? '✓ Payment verified' : 'Payment not verified yet'}
                  {' · '}{req.client.orders ? `Hired on Twasta ${req.client.orders} time${req.client.orders === 1 ? '' : 's'}, ${req.client.completed} finished` : 'New client on Twasta'}
                  {req.client.spent_minor > 0 ? ` · ${money(req.client.spent_minor, cur)} spent` : ''}
                </span>
              </div>
            )}

            <Messages requestId={rid} />
          </section>

          {/* ── the estimate ── */}
          <form onSubmit={onSend} className="mkt-panel mkt-col" style={{ flex: '999 1 560px', minWidth: 0, gap: 20, padding: 24 }} aria-label="Your estimate">
            <div className="mkt-col" style={{ gap: 4 }}>
              <h2 style={{ fontSize: 24 }}>Your estimate{quote ? ` · version ${quote.status === 'draft' ? quote.version : editing ? quote.version + 1 : quote.version}` : ''}</h2>
              <span className="mkt-muted" style={{ fontSize: 14 }}>
                {live ? 'This is what the client sees. Edit it to send a new version.' : 'Price what the brief asks for. You can edit or withdraw it until the client responds.'}
              </span>
            </div>

            <fieldset disabled={!formOn} style={{ border: 0, padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 20, minWidth: 0 }}>
              <div className="mkt-field">
                <label htmlFor="est-price">Your price to build it</label>
                <div className="mkt-row">
                  <div className="mkt-money"><span>{sym(cur)}</span><input id="est-price" inputMode="decimal" value={form.price} onChange={(e) => set({ price: e.target.value.replace(/[^\d.]/g, '') })} placeholder="e.g. 240000" style={{ fontSize: 18 }} /></div>
                  <span className="mkt-faint">One fixed price for everything you marked as included.</span>
                </div>
              </div>

              <div className="mkt-col" style={{ gap: 10 }}>
                <div className="mkt-row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <strong style={{ fontSize: 14 }}>Payment stages — Twasta’s standard terms</strong>
                  <span className="mkt-faint">The shares are fixed. You plan what you deliver at each stage and when.</span>
                </div>
                <PaymentStages stages={terms.stages} amountMinor={priceMinor} rows={serverRows || undefined} currency={cur} who="builder" advanceRelease={terms.advance_release} />
                <div className="mkt-scroll">
                  <table className="mkt-table" style={{ minWidth: 620 }}>
                    <thead><tr><th scope="col">Stage</th><th scope="col">What you deliver for approval</th><th scope="col" style={{ width: 90 }}>Weeks</th><th scope="col" style={{ width: 70 }}>Share</th><th scope="col" className="num">You’re paid</th></tr></thead>
                    <tbody>
                      {terms.stages.map((s, i) => (
                        <tr key={s.key || i}>
                          <td><strong>{s.name}</strong>{s.approves && <div className="mkt-faint">{s.approves}</div>}</td>
                          <td>
                            {i === 0
                              ? <span className="mkt-faint">Paid by the client when they order; paid to you {terms.advance_release === 'builder_accepts' ? 'when you accept the order' : 'with the first approval'}.</span>
                              : <input aria-label={`What you deliver for ${s.name}`} value={form.plan[i]?.deliverables || ''} placeholder="What the client approves at this stage"
                                  onChange={(e) => set({ plan: form.plan.map((p, j) => (j === i ? { ...p, deliverables: e.target.value } : p)) })}
                                  style={{ width: '100%', font: 'inherit', fontSize: 14, padding: '8px 10px', borderRadius: 8, border: '1px solid var(--color-border-strong, var(--color-border))', background: 'var(--color-input-bg, var(--color-bg))', color: 'var(--color-text)' }} />}
                          </td>
                          <td>
                            {i === 0 ? <span className="mkt-faint">—</span> : (
                              <input aria-label={`Weeks for ${s.name}`} inputMode="numeric" value={form.plan[i]?.weeks || ''}
                                onChange={(e) => set({ plan: form.plan.map((p, j) => (j === i ? { ...p, weeks: e.target.value.replace(/\D/g, '') } : p)) })}
                                style={{ width: 70, font: 'inherit', fontSize: 14, padding: '8px 10px', borderRadius: 8, border: '1px solid var(--color-border-strong, var(--color-border))', background: 'var(--color-input-bg, var(--color-bg))', color: 'var(--color-text)' }} />
                            )}
                          </td>
                          <td><span className="mkt-pill info">{s.pct}%</span></td>
                          <td className="num" style={{ fontWeight: 700 }}>{money(amounts[i] ?? 0, cur)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <span className="mkt-faint">
                  The client pays Twasta each stage’s share {terms.collect_on === 'approve' ? 'when they approve it' : 'when you submit it for approval'} (the advance when they order). Twasta pays you, less its {feePct}% fee, when the client approves
                  {terms.on_no_response === 'auto_approve' ? ` — or automatically ${terms.approve_days} days after you submit if they don’t respond.` : '.'}
                </span>
              </div>

              <div className="mkt-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
                <div className="mkt-field"><label htmlFor="est-start">Can start on</label><input id="est-start" type="date" value={form.start} onChange={(e) => set({ start: e.target.value })} /></div>
                <div className="mkt-col" style={{ gap: 6 }}><strong style={{ fontSize: 14 }}>Live in</strong><span style={{ fontSize: 22, fontWeight: 700 }}>{weeks ? `${weeks} week${weeks === 1 ? '' : 's'}` : '—'}</span></div>
              </div>

              <div className="mkt-col" style={{ gap: 8 }}>
                <strong style={{ fontSize: 14 }}>AI used while you build on Twasta</strong>
                <div className="mkt-choices" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
                  {([['included', 'Included in my price', 'I use my own AI or Twasta credits.'], ['client', 'Client’s AI credits', 'Lower price; the client’s credits pay for the AI used.']] as const).map(([id, t, d]) => (
                    <button key={id} type="button" aria-pressed={form.ai === id} className={`mkt-choice${form.ai === id ? ' on' : ''}`} onClick={() => set({ ai: id })}>
                      <strong>{t}</strong><span className="mkt-muted" style={{ fontSize: 13 }}>{d}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="mkt-col" style={{ gap: 10, border: '1px solid var(--color-border)', borderRadius: 12, padding: 16 }}>
                <strong style={{ fontSize: 14 }}>Monthly price to use it, once installed</strong>
                <span className="mkt-faint">The client pays this every month from their wallet, like any Marketplace app. It covers hosting, updates and fixes for their users.</span>
                <HostCallout currency={cur} />
                <div className="mkt-chips" role="group" aria-label="Monthly price basis">
                  {([['person', 'Per person'], ['server', 'Per server']] as const).map(([id, t]) => (
                    <button key={id} type="button" className={`mkt-chip${form.basis === id ? ' on' : ''}`} aria-pressed={form.basis === id} onClick={() => set({ basis: id })}>{t}</button>
                  ))}
                </div>
                {form.basis === 'server' && (
                  <div className="mkt-field" style={{ maxWidth: 360 }}>
                    <label htmlFor="est-size">Server size</label>
                    <select id="est-size" value={form.size} onChange={(e) => set({ size: e.target.value })}>
                      {!hosting && <option value={form.size}>Loading sizes…</option>}
                      {hosting?.sizes.map((s) => <option key={s.id} value={s.id}>{s.name} — up to {s.max_people} people</option>)}
                    </select>
                  </div>
                )}
                <div className="mkt-row">
                  <div className={`mkt-money${belowHost ? ' bad' : ''}`}><span>{sym(cur)}</span>
                    <input aria-label="Monthly price" aria-invalid={belowHost} inputMode="decimal" value={form.monthly} onChange={(e) => set({ monthly: e.target.value.replace(/[^\d.]/g, '') })} />
                  </div>
                  <span className="mkt-muted" style={{ fontSize: 14 }}>{form.basis === 'person' ? `per person a month${req.details?.users ? ` · the client has ${req.details.users} people` : ''}` : `a month for a ${size?.name || ''} server${size ? ` (up to ${size.max_people} people)` : ''}`}</span>
                </div>
                {hostMinor != null && (
                  <div className="mkt-row" style={{ gap: '6px 20px', background: 'var(--color-surface-2)', borderRadius: 8, padding: '10px 12px', fontSize: 14 }}>
                    <span><span className="mkt-faint">Hosting (Twasta)</span> <strong>{money(hostMinor, cur)}</strong> {form.basis === 'person' ? 'per person a month' : 'a month'}</span>
                    {monthlyMinor > hostMinor && <span><span className="mkt-faint">Above hosting</span> <strong style={{ color: 'var(--color-success)' }}>{money(monthlyMinor - hostMinor, cur)}</strong> <span className="mkt-faint">before the Marketplace fee</span></span>}
                  </div>
                )}
                {belowHost && <div role="alert" className="mkt-msg error">{form.basis === 'person' ? `Hosting costs ${money(hostMinor, cur)} per person — the price must be higher than that.` : `A ${size?.name || ''} server costs ${money(hostMinor, cur)} a month to host — the price must be higher than that.`}</div>}
              </div>

              {(req.questions || []).length > 0 && (
                <div className="mkt-col" style={{ gap: 10 }}>
                  <strong style={{ fontSize: 14 }}>The client’s questions</strong>
                  {req.questions.map((qn, i) => (
                    <div key={i} className="mkt-field">
                      <label htmlFor={`est-q${i}`}>{qn}</label>
                      <textarea id={`est-q${i}`} rows={2} value={form.answers[i] || ''} placeholder="Your answer" onChange={(e) => set({ answers: form.answers.map((a, j) => (j === i ? e.target.value : a)) })} />
                    </div>
                  ))}
                </div>
              )}

              <div className="mkt-field">
                <label htmlFor="est-note">Note to the client</label>
                <textarea id="est-note" rows={4} value={form.note} onChange={(e) => set({ note: e.target.value })} placeholder="Why you're a good fit, what you'd reuse, anything you left out and why." />
              </div>

              <div className="mkt-col" style={{ gap: 8 }}>
                <strong style={{ fontSize: 14 }}>Show similar work <span className="mkt-faint" style={{ fontWeight: 400 }}>— apps you built; a link lets the client try a live demo</span></strong>
                {form.demos.map((d, i) => (
                  <div key={i} className="mkt-row" style={{ gap: 8, alignItems: 'flex-end' }}>
                    <div className="mkt-field" style={{ flex: '1 1 160px' }}><label htmlFor={`demo-n${i}`}>Name</label><input id={`demo-n${i}`} value={d.name} onChange={(e) => set({ demos: form.demos.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)) })} /></div>
                    <div className="mkt-field" style={{ flex: '2 1 200px' }}><label htmlFor={`demo-u${i}`}>Link</label><input id={`demo-u${i}`} type="url" value={d.url} placeholder="https://" onChange={(e) => set({ demos: form.demos.map((x, j) => (j === i ? { ...x, url: e.target.value } : x)) })} /></div>
                    <div className="mkt-field" style={{ flex: '2 1 200px' }}><label htmlFor={`demo-t${i}`}>What it shows</label><input id={`demo-t${i}`} value={d.note} onChange={(e) => set({ demos: form.demos.map((x, j) => (j === i ? { ...x, note: e.target.value } : x)) })} /></div>
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => set({ demos: form.demos.filter((_, j) => j !== i) })} aria-label={`Remove ${d.name || 'this app'}`}>Remove</button>
                  </div>
                ))}
                {form.demos.length < 6 && <button type="button" className="btn btn-secondary btn-sm" style={{ alignSelf: 'flex-start' }} onClick={() => set({ demos: [...form.demos, { name: '', url: '', note: '' }] })}>+ Add an app</button>}
              </div>
            </fieldset>

            {/* summary + actions */}
            <div className="mkt-panel mkt-col" style={{ gap: 6, background: 'var(--color-bg)', padding: 18 }}>
              <strong style={{ fontSize: 17 }}>Summary</strong>
              <SumRow label="Your price" value={money(priceMinor, cur)} strong />
              <SumRow label={`Twasta fee (${feePct}%)`} value={`− ${money(feeMinor, cur)}`} />
              {tdsPct > 0 && <SumRow label={`TDS (${tdsPct}%)`} value={`− ${money(tdsMinor, cur)}`} />}
              <div className="mkt-row" style={{ justifyContent: 'space-between', fontSize: 18, paddingTop: 8, borderTop: '1px solid var(--color-border)' }}>
                <strong>You receive about</strong><strong style={{ color: 'var(--color-success)' }}>{money(Math.max(0, priceMinor - feeMinor - tdsMinor), cur)}</strong>
              </div>
              <span className="mkt-faint">
                Paid stage by stage as the client approves.{terms.builder_fee_tax === 'added' ? ' GST on the Twasta fee is also deducted.' : ''}
                {terms.client_fee_percent > 0 && ` The client also pays Twasta a ${terms.client_fee_percent}% service fee (${money(clientFeeMinor, cur)}) on top of your price.`}
              </span>
              {monthlyMinor > 0 && <SumRow label="Then every month" value={`${money(monthlyMinor, cur)} ${form.basis === 'person' ? 'per person' : 'a month'}`} />}
              {items.length > covered && formOn && <div className="mkt-msg warn">{items.length - covered} brief item{items.length - covered === 1 ? '' : 's'} marked not included — say why next to each one or in your note.</div>}
              {req.estimates_count > 0 && <span className="mkt-faint">{req.estimates_count} estimate{req.estimates_count === 1 ? '' : 's'} on this request so far.</span>}
              {terms.quote_valid_days > 0 && <span className="mkt-faint">A sent estimate stays valid for {terms.quote_valid_days} days.</span>}

              {problems.length > 0 && formOn && (
                <div role="alert" className="mkt-msg warn" style={{ marginTop: 6 }}>
                  <div>Before you send it:</div>
                  <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>{problems.map((p) => <li key={p}>{p}</li>)}</ul>
                </div>
              )}

              {formOn && (
                <div className="mkt-row" style={{ marginTop: 8 }}>
                  <button type="submit" className="btn btn-primary" disabled={!!busy}>{busy === 'send' ? 'Sending…' : quote && quote.status !== 'draft' ? 'Send the new version' : 'Send estimate'}</button>
                  <button type="button" className="btn btn-secondary" disabled={!!busy} onClick={onSave}>{busy === 'save' ? 'Saving…' : 'Save as draft'}</button>
                  {quote && quote.status !== 'draft' && <button type="button" className="btn btn-ghost" disabled={!!busy} onClick={load}>Cancel changes</button>}
                  {quote?.status === 'draft' && quote.version > 1 && <span className="mkt-faint">Version {quote.version - 1} stays with the client until you send this one.</span>}
                </div>
              )}
            </div>

            <div className="mkt-panel mkt-col" style={{ gap: 8, padding: 18, fontSize: 14 }}>
              <strong>If you’re hired</strong>
              <ul className="mkt-muted" style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <li>A new project is created in your own Twasta account for this order.</li>
                <li>The client tries a working copy of the app with sample data at every stage and approves it stage by stage.</li>
                <li>When they sign off the last stage, it is installed in their organization — their own copy and database.</li>
                <li>You keep it updated, and earn the monthly price for as long as they use it.</li>
              </ul>
            </div>
          </form>
        </div>
      </div>
    </MarketplaceShell>
  )
}

function Fact({ label, value }: { label: string; value: string }) {
  return <div><span className="mkt-faint">{label}</span><div style={{ fontWeight: 600 }}>{value}</div></div>
}

function SumRow({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="mkt-row" style={{ justifyContent: 'space-between', fontSize: 15, padding: '2px 0' }}>
      <span className="mkt-muted">{label}</span>{strong ? <strong>{value}</strong> : <span>{value}</span>}
    </div>
  )
}

function QuoteStatusPanel({ quote, justSent, canEdit, onEdit, confirmWithdraw, setConfirmWithdraw, onWithdraw, busy }: {
  quote: Quote; justSent: boolean; canEdit: boolean; onEdit: () => void
  confirmWithdraw: boolean; setConfirmWithdraw: (v: boolean) => void; onWithdraw: () => void; busy: boolean
}) {
  const withdrawable = ['sent', 'revision_asked'].includes(quote.status)
  const versions = quote.versions || []
  return (
    <section className={`mkt-panel mkt-col${justSent ? ' mkt-panel-accent' : ''}`} style={{ gap: 12 }} aria-label="Your estimate's status">
      <div className="mkt-row" style={{ justifyContent: 'space-between' }}>
        <div className="mkt-col" style={{ gap: 4 }}>
          <div className="mkt-row" style={{ gap: 8 }}>
            <strong style={{ fontSize: 19 }}>{justSent ? `Estimate sent — ${money(quote.price_minor, quote.currency)}${quote.weeks ? `, ${quote.weeks} weeks` : ''}` : `Your estimate: ${money(quote.price_minor, quote.currency)}${quote.weeks ? ` · ${quote.weeks} weeks` : ''}`}</strong>
            <QuotePill status={quote.status} />
            {quote.shortlisted && <span className="mkt-pill ok">Shortlisted</span>}
            {quote.expired && <span className="mkt-pill bad">Expired</span>}
          </div>
          <span className="mkt-muted" style={{ fontSize: 14 }}>
            Version {quote.version}{quote.sent_at ? ` · sent ${fmtDate(quote.sent_at)}` : ''}{quote.valid_until ? ` · valid until ${fmtDate(quote.valid_until)}` : ''}
            {justSent && '. The client gets it now; we’ll tell you when they shortlist you or send a message.'}
          </span>
        </div>
        <div className="mkt-row">
          {canEdit && <button type="button" className="btn btn-secondary" onClick={onEdit}>Edit estimate</button>}
          {withdrawable && !confirmWithdraw && <button type="button" className="btn btn-ghost" onClick={() => setConfirmWithdraw(true)}>Withdraw</button>}
          {justSent && <Link to="/marketplace/builder?tab=estimates" className="btn btn-ghost">Track my estimates</Link>}
        </div>
      </div>
      {confirmWithdraw && (
        <div role="alert" className="mkt-banner warn">
          <div><strong>Withdraw this estimate?</strong>The client will no longer be able to choose it. You can send a new one while the request is open.</div>
          <div className="mkt-row">
            <button type="button" className="btn btn-primary" disabled={busy} onClick={onWithdraw}>{busy ? 'Withdrawing…' : 'Yes, withdraw'}</button>
            <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => setConfirmWithdraw(false)}>Keep it</button>
          </div>
        </div>
      )}
      {quote.status === 'revision_asked' && quote.revision_note && (
        <div className="mkt-banner warn"><div><strong>The client asked for changes</strong>{quote.revision_note}</div></div>
      )}
      {canEdit && <span className="mkt-faint">Editing a sent estimate starts a new version; the client keeps seeing this one until you send it.</span>}
      {versions.length > 1 && (
        <div className="mkt-scroll">
          <table className="mkt-table" style={{ minWidth: 480 }}>
            <caption style={{ textAlign: 'left', fontWeight: 700, paddingBottom: 6 }}>Versions</caption>
            <thead><tr><th scope="col">Version</th><th scope="col">Sent</th><th scope="col" className="num">Price</th><th scope="col">Weeks</th><th scope="col">Status</th></tr></thead>
            <tbody>
              {versions.map((v) => (
                <tr key={v.id}><td>{v.version}</td><td>{v.sent_at ? fmtDate(v.sent_at) : '—'}</td><td className="num">{money(v.price_minor, quote.currency)}</td><td>{v.weeks ?? '—'}</td><td><QuotePill status={v.status} /></td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}

function Messages({ requestId }: { requestId: number }) {
  const [msgs, setMsgs] = useState<TimelineEvent[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [text, setText] = useState('')
  const [sending, setSending] = useState(false)
  const endRef = useRef<HTMLDivElement | null>(null)

  const load = useCallback(async () => {
    try {
      const r = await requestMessages(requestId)
      setMsgs(r.messages)
      setError(null)
    } catch (e) {
      setError(mktError(e, "We couldn't load the messages.").message)
    }
  }, [requestId])
  useEffect(() => { load() }, [load])
  const sorted = useMemo(() => (msgs || []).slice().sort((a, b) => a.at.localeCompare(b.at)), [msgs])
  useEffect(() => { endRef.current?.scrollIntoView({ block: 'nearest' }) }, [sorted.length])

  const send = async (e: FormEvent) => {
    e.preventDefault()
    const t = text.trim()
    if (!t) return
    setSending(true)
    try {
      const m = await sendRequestMessage(requestId, t)
      setMsgs((cur) => [...(cur || []), m])
      setText('')
    } catch (err) {
      toast.error(mktError(err, "Your message wasn't sent.").message)
    } finally {
      setSending(false)
    }
  }

  return (
    <section className="mkt-panel mkt-col" style={{ gap: 10, padding: 18 }} aria-label="Messages with the client">
      <div className="mkt-row" style={{ justifyContent: 'space-between' }}>
        <strong style={{ fontSize: 17 }}>Messages with the client</strong>
        <button type="button" className="btn btn-ghost btn-sm" onClick={load}>Refresh</button>
      </div>
      {error && <div role="alert" className="mkt-msg error">{error}</div>}
      {!msgs && !error && <span className="mkt-muted" role="status">Loading messages…</span>}
      {msgs && sorted.length === 0 && <span className="mkt-muted" style={{ fontSize: 14 }}>No messages yet. Ask the client anything the brief doesn’t answer.</span>}
      {sorted.length > 0 && (
        <div className="mkt-col" style={{ gap: 8, maxHeight: 340, overflowY: 'auto' }} aria-live="polite">
          {sorted.map((m) => m.kind === 'message' ? (
            <div key={m.id} className="mkt-col" style={{ gap: 2, background: 'var(--color-surface-2)', borderRadius: 10, padding: '8px 12px' }}>
              <span className="mkt-faint"><strong style={{ color: 'var(--color-text)' }}>{m.actor || 'Someone'}</strong> · {relTime(m.at)}</span>
              <span style={{ fontSize: 14, whiteSpace: 'pre-wrap' }}>{m.text}</span>
            </div>
          ) : (
            <span key={m.id} className="mkt-faint" style={{ textAlign: 'center' }}>{m.text} · {relTime(m.at)}</span>
          ))}
          <div ref={endRef} />
        </div>
      )}
      <form onSubmit={send} className="mkt-col" style={{ gap: 8 }}>
        <label htmlFor="msg-text" className="mkt-sr">Message to the client</label>
        <textarea id="msg-text" rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="Write a message to the client"
          style={{ font: 'inherit', fontSize: 14, padding: '9px 11px', borderRadius: 10, border: '1px solid var(--color-border-strong, var(--color-border))', background: 'var(--color-input-bg, var(--color-bg))', color: 'var(--color-text)' }} />
        <button type="submit" className="btn btn-secondary btn-sm" style={{ alignSelf: 'flex-end' }} disabled={sending || !text.trim()}>{sending ? 'Sending…' : 'Send message'}</button>
      </form>
    </section>
  )
}
